/**
 *
 * AuthModal/SignUpStepOne
 *
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import _get from 'lodash/get';
import _has from 'lodash/has';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  checkEmailAvailability,
  checkEmailAvailabilityResult,
} from 'containers/Auth/actions';
import { makeSelectEmailAvailability } from 'containers/Auth/selectors';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import PageIntroBlock from 'components/PageIntroBlock';
import PasswordTextField from 'components/PasswordTextField';
import ProfileTextField from 'components/ProfileTextField';
import FacebookButton from 'components/SocialButtons/FacebookButton';
import GoogleButton from 'components/SocialButtons/GoogleButton';
import SAMLButton from 'components/SAMLButton';
import MicrosoftButton from 'components/SocialButtons/MicrosoftButton';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { emailRegex, getLowerCaseEmailDomain } from 'utils/stringUtils';

const useStyles = makeStyles(theme => ({
  caption: {
    ...theme.typography.overline,
    color: theme.palette.text.primary,
  },
  captionLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:focus': {
      outlineWidth: 0,
    },
    ...theme.typography.overline,
  },
  socialLoginContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
  },
  dividerItem: {
    flex: 1,
    height: 1,
    backgroundColor: '#CECECE',
  },
  dividerText: {
    paddingLeft: 8,
    paddingRight: 8,
    width: '50%',
    textAlign: 'center',
    ...theme.typography.pxSmall,
    fontSize: '0.875rem',
  },
  formContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    textTransform: 'uppercase',
    // marginTop: 16,
    marginBottom: 5,
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
}));

const resetCheckEmailAvailability = () => checkEmailAvailabilityResult(null);

const SignUpStepOne = ({
  authOptions,
  shouldLimitEmailDomains,
  shouldDisplaySAML,
  requiredEmailDomains,
  onCaptionClick,
  onSocialLogin,
  onSubmit,
  hasClient,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const emailAvailability = useSelector(makeSelectEmailAvailability());
  const [signUpSiteCopy] = useSiteCopySelector(['sign-up']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState([]);

  useEffect(() => {
    if (_has(emailAvailability, 'error')) {
      toast('Ops. Something went wrong. Please, try again.', {
        type: 'error',
      });
    } else if (_has(emailAvailability, 'exists')) {
      toast('Email is already linked to an account. Try signing in.', {
        type: 'error',
      });
    } else if (_has(emailAvailability, 'available')) {
      onSubmit(email, password);
    }

    return () => dispatch(resetCheckEmailAvailability());
  }, [dispatch, email, emailAvailability, password, onSubmit]);

  const handleFormChange = e => {
    if (e.target.name === 'email') {
      setEmail(e.target.value);
      if (error) {
        setError(error.filter(item => item !== 'email'));
      } else if (e.target.value === '') {
        setError(error.concat('email'));
      }
    }
    if (e.target.name === 'password') {
      setPassword(e.target.value);
      if (error) {
        setError(error.filter(item => item !== 'password'));
      } else if (e.target.value === '') {
        setError(error.concat('password'));
      }
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    const formErrors = [];

    if (!emailRegex.test(email)) {
      formErrors.push('email');
    }
    if (password === '') {
      formErrors.push('password');
    }

    if (formErrors.length === 0) {
      const isDomainAllowed =
        !shouldLimitEmailDomains ||
        requiredEmailDomains.includes(getLowerCaseEmailDomain(email));
      if (isDomainAllowed) dispatch(checkEmailAvailability(email));
      else {
        toast('Sorry, this email domain is not allowed to access this site.', {
          type: 'error',
        });
      }
    } else {
      setError(formErrors);
    }
  };

  const shouldDisplayGoogle = authOptions.google;
  const shouldDisplayFacebook = authOptions.facebook;
  const shouldDisplayMicrosoft = authOptions.ms;
  const shouldDisplayOAUthOptions =
    shouldDisplayGoogle ||
    shouldDisplayFacebook ||
    shouldDisplayMicrosoft ||
    shouldDisplaySAML;

  return (
    <>
      <Grid item xs={12}>
        <PageIntroBlock type="common" page="sign-up" modal />
        <Typography
          variant="caption"
          align="center"
          className={classes.caption}
        >
          {_get(signUpSiteCopy, 'pageCopy.userTerms', []).map(el => {
            if (el.action) {
              return (
                <span
                  key={el.value}
                  role="link"
                  tabIndex={0}
                  onKeyPress={() => onCaptionClick(el.action)}
                  className={classes.captionLink}
                  onClick={() => onCaptionClick(el.action)}
                >
                  {el.value}
                </span>
              );
            }

            return (
              <span key={el.value} className={classes.caption}>
                {el.value}
              </span>
            );
          })}
        </Typography>
      </Grid>
      {shouldDisplayOAUthOptions && (
        <Grid item xs={12} className={classes.socialLoginContainer}>
          {shouldDisplayGoogle && (
            <GoogleButton
              onClick={() => onSocialLogin('google')}
              text={_get(signUpSiteCopy, 'pageCopy.googleLabel')}
            />
          )}
          {shouldDisplayFacebook && (
            <FacebookButton
              onClick={() => onSocialLogin('facebook')}
              text={_get(signUpSiteCopy, 'pageCopy.facebookLabel')}
            />
          )}
          {shouldDisplayMicrosoft && (
            <MicrosoftButton
              onClick={() => onSocialLogin('microsoft.com')}
              text={_get(signUpSiteCopy, 'pageCopy.microsoftLabel')}
            />
          )}
          <SAMLButton />
        </Grid>
      )}
      {shouldDisplayOAUthOptions && authOptions.cm && hasClient && (
        <Grid item xs={12} className={classes.divider}>
          <div className={classes.dividerItem} />
          <Typography className={classes.dividerText} color="textPrimary">
            {_get(signUpSiteCopy, 'pageCopy.emailModalCallToAction')}
          </Typography>
          <div className={classes.dividerItem} />
        </Grid>
      )}
      {authOptions.cm && hasClient && (
        <Grid item xs={12}>
          <form
            className={classes.formContainer}
            onSubmit={handleSubmit}
            noValidate
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ProfileTextField
                  field="email"
                  plain
                  value={email}
                  onChange={handleFormChange}
                  pattern="^\\S+@\\S+\\.\\S+$"
                  required
                  type="email"
                  error={error.includes('email')}
                />
              </Grid>
              <Grid item xs={12}>
                <PasswordTextField
                  value={password}
                  onChange={handleFormChange}
                  plain
                  required
                  error={
                    error.includes('password')
                      ? _get(signUpSiteCopy, 'pageCopy.passwordError')
                      : false
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <ButtonBase className={classes.button} type="submit">
                  {_get(signUpSiteCopy, 'pageCopy.cmLabel')}
                </ButtonBase>
              </Grid>
            </Grid>
          </form>
        </Grid>
      )}
    </>
  );
};

SignUpStepOne.propTypes = {
  authOptions: PropTypes.object,
  onCaptionClick: PropTypes.func,
  onSocialLogin: PropTypes.func,
  hasClient: PropTypes.bool,
  shouldDisplaySAML: PropTypes.bool,
  shouldLimitEmailDomains: PropTypes.bool,
  requiredEmailDomains: PropTypes.array,
};

export default SignUpStepOne;
